import { useCallback, useEffect, useMemo } from "react"
import type React from "react"

import { TRACKING_EVENTS, type useAnalytics } from "@/lib/analytics"
import { useCollectionPageShortcuts } from "@/hooks/useCollectionPageShortcuts"
import { writePersistedNavigationItems } from "@/hooks/usePersistedPageNavigation"
import type { RunRowLike } from "@/types/RunRow"

interface UseRunResultsRowNavigationParams {
  getResultHref: (simulationOutputId: string) => string
  markAsInvestigated: (simulationOutputId: string) => void
  navigationScopeStorageKey?: string
  onRowClick: (simulationOutputId: string) => void
  onRowHover?: (simulationOutputId: string | null) => void
  runId?: string
  scopedRunRows: RunRowLike[]
  trackEvent: ReturnType<typeof useAnalytics>["trackEvent"]
}

export function useRunResultsRowNavigation({
  getResultHref,
  markAsInvestigated,
  navigationScopeStorageKey,
  onRowClick,
  onRowHover,
  runId,
  scopedRunRows,
  trackEvent,
}: UseRunResultsRowNavigationParams) {
  const visibleSimulationRows = useMemo(
    () =>
      scopedRunRows.filter(
        (item): item is RunRowLike & { simulation_output_id: string } =>
          typeof item.simulation_output_id === "string" &&
          item.simulation_output_id.length > 0,
      ),
    [scopedRunRows],
  )

  const persistNavigationScope = useCallback(() => {
    if (!navigationScopeStorageKey) {
      return
    }

    writePersistedNavigationItems(
      navigationScopeStorageKey,
      scopedRunRows
        .map((row) => {
          if (
            typeof row.simulation_output_id !== "string" ||
            row.simulation_output_id.length === 0
          ) {
            return null
          }

          const parentId =
            typeof row.run_id === "string" && row.run_id.length > 0
              ? row.run_id
              : runId

          return parentId
            ? {
                id: row.simulation_output_id,
                parentId,
              }
            : null
        })
        .filter(
          (item): item is { id: string; parentId: string } => item !== null,
        ),
    )
  }, [navigationScopeStorageKey, runId, scopedRunRows])

  const openSimulationRow = useCallback(
    (item: RunRowLike & { simulation_output_id: string }) => {
      persistNavigationScope()
      markAsInvestigated(item.simulation_output_id)
      trackEvent(TRACKING_EVENTS.RUNS.OUTPUT_VIEWED, {
        run_id: runId,
        simulation_output_id: item.simulation_output_id,
      })
      onRowClick(item.simulation_output_id)
    },
    [markAsInvestigated, onRowClick, persistNavigationScope, trackEvent, runId],
  )

  const {
    activeItemId: visibleSimulationOutputId,
    handlePointerEnter,
    handlePointerLeave,
  } = useCollectionPageShortcuts({
    getItemId: (item) => item.simulation_output_id,
    itemLabel: "simulation",
    items: visibleSimulationRows,
    onOpenItem: openSimulationRow,
  })

  useEffect(() => {
    onRowHover?.(visibleSimulationOutputId)
  }, [onRowHover, visibleSimulationOutputId])

  useEffect(() => {
    return () => {
      onRowHover?.(null)
    }
  }, [onRowHover])

  const handleRowClick = useCallback(
    (simulationOutputId: string, event: React.MouseEvent) => {
      if (!simulationOutputId) {
        return
      }

      persistNavigationScope()
      markAsInvestigated(simulationOutputId)

      if (event.metaKey || event.ctrlKey) {
        window.open(getResultHref(simulationOutputId), "_blank")
      } else {
        onRowClick(simulationOutputId)
      }
    },
    [getResultHref, markAsInvestigated, onRowClick, persistNavigationScope],
  )

  return {
    handlePointerEnter,
    handlePointerLeave,
    handleRowClick,
    visibleSimulationOutputId,
  }
}
